import {
  CreditCard,
  Users,
  Calendar,
  DollarSign,
  ClipboardList,
  UtensilsCrossed,
  ShoppingBag,
  ShieldCheck,
  BarChart3,
  Receipt,
  Sandwich,
  Package,
  FileText,
  Factory,
  TrendingUp,
  Wrench,
  Layers,
  Warehouse,
  HandCoins,
  UsersRound,
} from "lucide-react";
import { Link } from "react-router-dom";
import { canAccessRoute } from "../../utils/route-permissions";
import { Skeleton } from "../common/ui/skeleton/skeleton";
import type { QuickLink, QuickLinksGridProps } from "../../types/dashboard";

const ALL_LINKS: QuickLink[] = [
  {
    label: "Ventas de Planes",
    href: "/plan-payments",
    Icon: CreditCard,
    description: "Registrar pagos de planes",
    requiredModule: "planSales",
  },
  {
    label: "Clientes",
    href: "/clients",
    Icon: Users,
    description: "Gestión de clientes",
  },
  {
    label: "Suscripciones",
    href: "/subscriptions",
    Icon: Calendar,
    description: "Ver suscripciones activas",
    requiredModule: "planSales",
  },
  {
    label: "Planes",
    href: "/plans",
    Icon: ClipboardList,
    description: "Administrar planes",
    requiredModule: "planSales",
  },
  {
    label: "Ventas de Comida",
    href: "/food-sales",
    Icon: UtensilsCrossed,
    description: "Registrar ventas de comida",
    requiredModule: "foodSales",
  },
  {
    label: "Ventas de Mercadería",
    href: "/merchandise-sales",
    Icon: ShoppingBag,
    description: "Registrar ventas de mercadería",
    requiredModule: "merchandiseSales",
  },
  {
    label: "Alimentos",
    href: "/foods",
    Icon: Sandwich,
    description: "Ver y gestionar alimentos",
    requiredModule: "foodSales",
  },
  {
    label: "Productos Disponibles",
    href: "/merchandise",
    Icon: Package,
    description: "Ver y gestionar productos",
    requiredModule: "merchandiseSales",
  },
  {
    label: "Control de Acceso",
    href: "/access-control",
    Icon: ShieldCheck,
    description: "Control de entrada/salida",
    requiredModule: "accessControl",
  },
  {
    label: "Órdenes de Venta",
    href: "/sales-orders",
    Icon: FileText,
    description: "Gestionar órdenes B2B",
    requiredModule: "salesOrders",
  },
  {
    label: "Facturas",
    href: "/invoices",
    Icon: Receipt,
    description: "Gestionar facturas B2B",
    requiredModule: "invoicing",
  },
  {
    label: "Métricas B2B",
    href: "/production-metrics",
    Icon: TrendingUp,
    description: "Analítica de producción",
    requiredModule: "productionMetrics",
  },
  {
    label: "Ingresos Unificados",
    href: "/unified-incomes",
    Icon: DollarSign,
    description: "Vista consolidada de ingresos",
    requiredAnyModule: ["planSales", "foodSales", "merchandiseSales"],
  },
  {
    label: "Métricas",
    href: "/metrics",
    Icon: BarChart3,
    description: "Analítica del negocio",
    requiredModule: "metrics",
  },
  {
    label: "Gastos",
    href: "/expenses",
    Icon: Receipt,
    description: "Registrar y ver gastos",
    requiredModule: "expenses",
  },
  {
    label: "Materiales",
    href: "/materials",
    Icon: Layers,
    description: "Gestionar materias primas",
    requiredModule: "productCatalog",
  },
  {
    label: "Productos",
    href: "/products",
    Icon: Factory,
    description: "Catálogo de productos",
    requiredModule: "productCatalog",
  },
  {
    label: "Maquinaria",
    href: "/machinery",
    Icon: Wrench,
    description: "Equipamiento y mantención",
    requiredModule: "machinery",
  },
  {
    label: "Inventario",
    href: "/inventory",
    Icon: Warehouse,
    description: "Control de stock",
    requiredModule: "inventory",
  },
  {
    label: "Otros Ingresos",
    href: "/other-incomes",
    Icon: HandCoins,
    description: "Registrar ingresos adicionales",
    requiredModule: "otherIncome",
  },
  {
    label: "CRM B2B",
    href: "/b2b-crm",
    Icon: UsersRound,
    description: "Gestión de clientes B2B",
    requiredModule: "b2bCrm",
  },
];

const SKELETON_COUNT = 8;

const QuickLinkSkeleton = () => (
  <div className="flex flex-col gap-2 sm:gap-3 p-3 sm:p-4 rounded-xl border border-global-primary bg-global-overlay">
    <Skeleton className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl" />
    <div className="min-w-0 space-y-1.5">
      <Skeleton className="h-3.5 sm:h-4 w-3/4" />
      <Skeleton className="h-2.5 sm:h-3 w-full" />
    </div>
  </div>
);

export const QuickLinksGrid = ({
  role,
  modulesSettings,
  isLoading,
}: QuickLinksGridProps) => {
  const visibleLinks = ALL_LINKS.filter((link) => {
    if (!canAccessRoute(role, link.href)) return false;
    if (link.requiredModule && !modulesSettings?.[link.requiredModule]?.enabled)
      return false;
    if (
      link.requiredAnyModule &&
      !link.requiredAnyModule.some((mod) => modulesSettings?.[mod]?.enabled)
    )
      return false;
    return true;
  });

  return (
    <div className="card-compact">
      <h2 className="text-size-base font-semibold text-global-title mb-3 sm:mb-5">
        Acceso Rápido
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-5 gap-2 sm:gap-compact">
        {isLoading
          ? Array.from({ length: SKELETON_COUNT }).map((_, i) => (
              <QuickLinkSkeleton key={i} />
            ))
          : visibleLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="flex flex-col gap-2 sm:gap-3 p-3 sm:p-4 rounded-xl border border-global-primary bg-global-overlay hover:bg-global-accent-overlay hover:border-global-accent-ring hover:scale-[1.04] hover:-translate-y-0.5 active:scale-[0.97] hover:shadow-md transition-all duration-200 min-w-0"
              >
                <span className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-global-overlay-secondary border border-global-primary flex items-center justify-center text-global-accent shrink-0">
                  <link.Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                </span>
                <div className="min-w-0">
                  <p className="text-size-xs sm:text-size-sm font-semibold text-global-title leading-tight line-clamp-2">
                    {link.label}
                  </p>
                  <p className="text-size-2xs sm:text-size-xs text-global-muted mt-0.5 leading-snug line-clamp-2">
                    {link.description}
                  </p>
                </div>
              </Link>
            ))}
      </div>
    </div>
  );
};
